import { AuthContext } from '@/contexts/AuthContext';
import {
  User,
  useWhoamiQuery,
  useSetCookieConsentMutation,
} from '@/generated/graphql';
import { createLogger } from '@/utils/createLogger';
import { useCallback, useEffect } from 'react';
import useLocalStorage from 'use-local-storage';
import { useLogin } from '@/hooks/useLogin';
import { useLogout } from '@/hooks/useLogout';
import { useSignup } from '@/hooks/useSignup';
import { useQuickSignup } from '@/hooks/useQuickSignup';
import { loadCookieConsent, clearCookieConsent } from '@/lib/cookies';
import { useJWT } from '@/hooks/useAuth';

const logger = createLogger('AuthProvider');

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Store user in localStorage for persistence
  const [user, setUser] = useLocalStorage<User | null>('user', null);

  // Use JWT utilities
  const { accessToken } = useJWT();

  // Verify the user is logged in via GraphQL
  const { data, refetch } = useWhoamiQuery({
    skip: !accessToken, // Skip query if no access token
  });
  const [setCookieConsent] = useSetCookieConsentMutation();

  // Update user when whoami query returns data
  useEffect(() => {
    if (!data) {
      return;
    }

    if (data.whoami) {
      setUser(data.whoami);

      // Migrate cookie consent from local storage to backend if needed
      if (!data.whoami.cookieConsent) {
        const localConsent = loadCookieConsent();
        if (localConsent && localConsent.hasConsented) {
          logger.info('Migrating cookie consent from local storage to backend');

          setCookieConsent({
            variables: {
              preferences: localConsent.preferences,
            },
          })
            .then(() => {
              logger.info('Successfully migrated cookie consent to backend');
              clearCookieConsent();
            })
            .catch((error) => {
              logger.error(
                'Failed to migrate cookie consent to backend:',
                error,
              );
              // Don't clear local storage on error to preserve consent
            });
        }
      }
    } else {
      logger.error('User is not logged in');
      setUser(null);
    }
  }, [data, setUser, setCookieConsent]);

  // Refetch user data when access token changes
  useEffect(() => {
    if (accessToken) {
      refetch();
    } else {
      setUser(null);
    }
  }, [accessToken, refetch, setUser]);

  const { login: loginHook, error: loginError } = useLogin();
  const { logout: logoutHook } = useLogout();
  const { signup: signupHook, error: signupError } = useSignup();
  const { quickSignup: quickSignupHook, error: quickSignupError } =
    useQuickSignup();

  const logout = useCallback(async () => {
    try {
      const result = await logoutHook();
      setUser(null);
      return result?.success || false;
    } catch (e) {
      logger.error(`Logout: error: `, e);
      setUser(null); // Clear user even if logout fails
      return false;
    }
  }, [logoutHook, setUser]);

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const result = await loginHook(email, password);

        if (result?.success && result.user) {
          setUser(result.user);
          return { user: result.user };
        } else {
          logger.error(`Login failed. Error: `, loginError || 'Unknown error');
          throw new Error(loginError || 'Login failed');
        }
      } catch (e) {
        logger.error(`Login: error: `, e);
        throw e;
      }
    },
    [loginHook, loginError, setUser],
  );

  const signup = useCallback(
    async (email: string, password: string) => {
      try {
        const result = await signupHook(email, password);

        if (result?.success && result.user) {
          setUser(result.user);
          return { user: result.user };
        } else {
          logger.error(
            `Signup failed. Error: `,
            signupError || 'Unknown error',
          );
          throw new Error(signupError || 'Signup failed');
        }
      } catch (e) {
        logger.error(`Signup: error: `, e);
        throw e;
      }
    },
    [signupHook, signupError, setUser],
  );

  const quickSignup = useCallback(
    async (name: string) => {
      try {
        const result = await quickSignupHook(name);

        if (result?.success && result.user) {
          setUser(result.user);
          return { user: result.user };
        } else {
          logger.error(
            `Quick signup failed. Error: `,
            quickSignupError || 'Unknown error',
          );
          throw new Error(quickSignupError || 'Quick signup failed');
        }
      } catch (e) {
        logger.error(`Quick signup: error: `, e);
        throw e;
      }
    },
    [quickSignupHook, quickSignupError, setUser],
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        logout,
        login,
        signup,
        quickSignup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
